import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { HelpCircle, ChevronDown, Phone, Mail } from 'lucide-react';

const FAQ = () => {
  const { i18n } = useTranslation();
  const [openIndex, setOpenIndex] = useState(0);

  const faqData = {
    fr: {
      title: "Questions Fréquentes",
      subtitle: "Trouvez des réponses à vos questions sur le Marathon de l'Avenir EDENLIFE 2026",
      categories: [
        {
          name: "Participation et Billets",
          questions: [
            {
              question: "Comment participer au Marathon de l'Avenir EDENLIFE 2026 ?",
              answer: "C'est simple ! Voici les étapes :\n\n1. Créez un compte gratuit sur notre plateforme avec votre email\n2. Connectez-vous à votre espace personnel\n3. Achetez vos billets (50 USD l'unité ou 3 pour 100 USD)\n4. Payez via Stripe, PayPal, Interac, Moncash, Natcash ou Crypto\n5. Recevez immédiatement vos numéros de billets par email avec un QR code\n\nC'est tout ! Vous êtes automatiquement inscrit pour le tirage au sort de décembre 2026."
            },
            {
              question: "Combien coûte un billet et quelles sont les offres ?",
              answer: "Nos tarifs sont simples et avantageux :\n\n• 1 billet : 50 USD\n• 3 billets : 100 USD (économisez 50 USD !)\n\nPlus vous achetez de billets, plus vos chances de gagner augmentent. Vous pouvez acheter jusqu'à 100 billets par transaction. Chaque billet a un numéro unique et une chance égale de gagner."
            },
            {
              question: "Puis-je acheter des billets pour quelqu'un d'autre ?",
              answer: "Chaque compte est personnel et les billets sont associés au compte de l'acheteur. Si vous souhaitez offrir des billets à quelqu'un, cette personne doit créer son propre compte. Vous pouvez ensuite lui transférer l'argent pour qu'elle effectue son propre achat. Les billets ne sont pas transférables après achat."
            },
            {
              question: "Y a-t-il une limite d'achat de billets ?",
              answer: "Vous pouvez acheter jusqu'à 100 billets par transaction. Il n'y a pas de limite sur le nombre total de billets que vous pouvez posséder. Plus vous avez de billets, plus vos chances de gagner sont élevées !"
            }
          ]
        },
        {
          name: "Prix et Tirage au Sort",
          questions: [
            {
              question: "Quels sont les prix à gagner ?",
              answer: "Nous offrons 5 prix exceptionnels :\n\n🏠 1er Prix - Maison Autosuffisante (450 000 USD)\n• Maison complètement meublée\n• Système d'énergie solaire 10kW intégré\n• 3 chambres, 2 salles de bain\n• 6 électroménagers inclus\n• Salon et salle à manger\n\n🚜 2ème Prix - Tracteur Agricole (40 000 USD)\n• Tracteur-Semeur diesel 4RM de 180 CV\n• Transmission multi-vitesses\n• Adaptabilité supérieure et puissance élevée\n• Pour l'agriculture moderne à grande échelle et travaux lourds\n\n🏍️ 3ème Prix - Grand Tricycle Cargo (5 000 USD)\n• Tricycle Cargo 350CC de puissance\n• Toute équipée, prêt à l'emploi\n• Idéal pour le transport de marchandises\n• Démarrez votre propre entreprise\n\n💻 4ème Prix - Super Laptop HP Envy (3 000 USD)\n• Processeur Intel i7-13700H 14 cœurs\n• Écran tactile IPS Full HD 17,3 pouces\n• 64 Go DDR4, SSD 4 To\n• Carte graphique Intel Iris Xe\n• Clavier rétroéclairé, Thunderbolt 4\n• Wi-Fi 6E, caméra infrarouge 5 MP\n• HDMI 2.1, Windows 10\n\n📺 5ème Prix - TV LG OLED 65\" (2 000 USD)\n• Téléviseur intelligent LG G5 OLED 4K UHD HDR\n• 65 pouces (OLED65G5SUB) – 2025\n• Qualité d'image exceptionnelle\n• Fonctionnalités smart TV avancées"
            },
            {
              question: "Quand et comment se déroule le tirage au sort ?",
              answer: "Le tirage au sort aura lieu en décembre 2026, à une date précise qui sera annoncée à tous les participants.\n\n• Le tirage est effectué de manière aléatoire par un système certifié\n• Un notaire ou huissier peut être présent pour garantir la transparence\n• Le tirage peut être diffusé en direct sur nos réseaux sociaux\n• Les résultats seront publiés sur notre site web et envoyés par email\n• Les gagnants seront contactés personnellement dans les 7 jours"
            },
            {
              question: "Comment savoir si j'ai gagné ?",
              answer: "Si vous gagnez, vous serez contacté de plusieurs façons :\n\n1. Email de notification envoyé à l'adresse de votre compte\n2. Appel téléphonique de notre équipe\n3. Publication des numéros gagnants sur la page 'Gagnants' de notre site\n4. Annonce sur nos réseaux sociaux\n\nAssurez-vous que vos coordonnées sont à jour dans votre profil !"
            },
            {
              question: "Puis-je échanger un prix contre de l'argent ?",
              answer: "Non, les prix ne peuvent pas être échangés contre de l'argent comptant. Les prix seront remis tels que décrits. Cependant, une fois que vous avez reçu votre prix, vous êtes libre d'en disposer comme vous le souhaitez."
            }
          ]
        },
        {
          name: "Paiement et Sécurité",
          questions: [
            {
              question: "Quels moyens de paiement sont acceptés ?",
              answer: "Nous acceptons plusieurs moyens de paiement, tous disponibles :\n\n💳 Carte bancaire internationale (Stripe)\n• Visa, Mastercard, American Express\n• Paiement sécurisé via Stripe\n\n💰 PayPal\n• Paiement en USD via PayPal\n\n🇨🇦 Virement Interac (e-Transfer)\n• Pour les résidents du Canada\n• Paiement en CAD\n\n📱 MONCASH\n• Paiement mobile pour Haïti\n• En Gourdes (HTG)\n\n📱 NATCASH\n• Paiement mobile pour Haïti\n• En Gourdes (HTG)\n\n₿ Cryptomonnaies\n• Bitcoin, Ethereum, USDT et autres\n• Via NOWPayments\n\nToutes les transactions sont cryptées et sécurisées."
            },
            {
              question: "Le paiement est-il sécurisé ?",
              answer: "Absolument ! Votre sécurité est notre priorité :\n\n• Paiements traités par Stripe, leader mondial du paiement en ligne\n• Cryptage SSL/TLS de bout en bout\n• Nous ne stockons JAMAIS vos données de carte bancaire\n• Conformité aux normes PCI-DSS\n• Confirmation instantanée par email après chaque achat"
            },
            {
              question: "Puis-je obtenir un remboursement ?",
              answer: "Les achats de billets sont définitifs et non remboursables. En achetant un billet, vous participez à une collecte de fonds caritative.\n\nException : En cas de problème technique avéré (double facturation, erreur système), contactez-nous dans les 24 heures avec la preuve du problème. Chaque cas sera examiné individuellement.\n\n📧 support@marathon-edenlifeintl.org\n📞 1 866-722-7007"
            }
          ]
        },
        {
          name: "Programme d'Affiliation",
          questions: [
            {
              question: "Comment fonctionne le programme d'affiliation ?",
              answer: "Devenez affilié et gagnez de l'argent en partageant !\n\n1. Connectez-vous à votre compte\n2. Accédez à l'Espace Affilié dans votre dashboard\n3. Obtenez votre code de parrainage unique\n4. Partagez votre lien sur les réseaux sociaux ou par email\n5. Gagnez 10% de commission sur chaque vente réalisée\n\nC'est 100% gratuit et sans engagement !"
            },
            {
              question: "Combien puis-je gagner comme affilié ?",
              answer: "Votre potentiel de gain est illimité !\n\n• Commission : 10% sur chaque vente via votre code\n• Exemple : Si quelqu'un achète 3 billets (100 USD), vous gagnez 10 USD\n• Calcul automatique après chaque vente validée\n• Paiement mensuel des commissions\n• Minimum de retrait : 100 USD\n\nPlus vous partagez, plus vous gagnez !"
            },
            {
              question: "Quels outils sont disponibles pour les affiliés ?",
              answer: "Nous vous fournissons tout le nécessaire :\n\n• Code de parrainage personnel et unique\n• Lien personnalisé à partager\n• Bannières promotionnelles à télécharger\n• Tableau de bord avec vos statistiques en temps réel\n• Suivi de vos ventes et commissions\n• Support dédié pour les affiliés"
            }
          ]
        },
        {
          name: "Compte et Support",
          questions: [
            {
              question: "Comment créer un compte ?",
              answer: "Créer un compte est rapide et gratuit :\n\n1. Cliquez sur 'Connexion' en haut de la page\n2. Choisissez 'Inscription'\n3. Remplissez vos informations (nom, email, mot de passe)\n4. Ou connectez-vous directement avec Google\n5. Validez votre inscription\n\nVous pouvez immédiatement acheter des billets !"
            },
            {
              question: "J'ai oublié mon mot de passe, que faire ?",
              answer: "Pas de panique ! Contactez notre support à support@marathon-edenlifeintl.org avec l'adresse email de votre compte. Nous vous aiderons à réinitialiser votre mot de passe rapidement."
            },
            {
              question: "Comment contacter le support ?",
              answer: "Notre équipe est là pour vous aider :\n\n📧 Email : support@marathon-edenlifeintl.org\n📞 Téléphone : 1 866-722-7007\n🕐 Horaires : Lun-Ven, 9h-17h (EST)\n\nNous répondons généralement sous 24-48 heures ouvrables.\n\nLangues de support : Français, English, Kreyòl"
            }
          ]
        }
      ],
      needMoreHelp: "Besoin d'aide supplémentaire ?",
      needMoreHelpDesc: "Notre équipe est là pour vous aider. N'hésitez pas à nous contacter !",
      contactButton: "Nous contacter"
    },
    en: {
      title: "Frequently Asked Questions",
      subtitle: "Find answers to your questions about the EDENLIFE Marathon 2026",
      categories: [
        {
          name: "Participation and Tickets",
          questions: [
            {
              question: "How do I participate in the EDENLIFE Marathon 2026?",
              answer: "It's simple! Here are the steps:\n\n1. Create a free account on our platform with your email\n2. Log in to your personal space\n3. Buy your tickets ($50 each or 3 for $100)\n4. Pay via Stripe, PayPal, Interac, Moncash, Natcash or Crypto\n5. Immediately receive your ticket numbers by email with a QR code\n\nThat's it! You're automatically entered for the December 2026 draw."
            },
            {
              question: "How much does a ticket cost and what are the offers?",
              answer: "Our prices are simple and advantageous:\n\n• 1 ticket: $50 USD\n• 3 tickets: $100 USD (save $50!)\n\nThe more tickets you buy, the higher your chances of winning. You can buy up to 100 tickets per transaction. Each ticket has a unique number and an equal chance of winning."
            },
            {
              question: "Can I buy tickets for someone else?",
              answer: "Each account is personal and tickets are associated with the buyer's account. If you want to gift tickets to someone, that person must create their own account. You can then transfer the money so they can make their own purchase. Tickets are not transferable after purchase."
            },
            {
              question: "Is there a limit on ticket purchases?",
              answer: "You can buy up to 100 tickets per transaction. There is no limit on the total number of tickets you can own. The more tickets you have, the higher your chances of winning!"
            }
          ]
        },
        {
          name: "Prizes and Draw",
          questions: [
            {
              question: "What are the prizes?",
              answer: "We offer 5 exceptional prizes:\n\n🏠 1st Prize - Self-Sufficient House ($450,000 USD)\n• Fully furnished house\n• Integrated 10kW solar energy system\n• 3 bedrooms, 2 bathrooms\n• 6 appliances included\n• Living room and dining room\n\n🚜 2nd Prize - Agricultural Tractor ($40,000 USD)\n• 180 HP 4WD diesel seeder tractor\n• Multi-speed transmission\n• Superior adaptability and high power\n• For large-scale modern agriculture and heavy-duty work\n\n🏍️ 3rd Prize - Large Cargo Tricycle ($5,000 USD)\n• 350CC powered Cargo Tricycle\n• Fully equipped, ready to use\n• Ideal for goods transport\n• Start your own business\n\n💻 4th Prize - Super HP Envy Laptop ($3,000 USD)\n• Intel i7-13700H 14-core processor\n• 17.3\" Full HD IPS touchscreen\n• 64GB DDR4, 4TB SSD\n• Intel Iris Xe graphics\n• Backlit keyboard, Thunderbolt 4\n• Wi-Fi 6E, 5MP infrared camera\n• HDMI 2.1, Windows 10\n\n📺 5th Prize - LG OLED 65\" TV ($2,000 USD)\n• LG G5 4K UHD HDR OLED Smart TV\n• 65-inch (OLED65G5SUB) – 2025\n• Exceptional picture quality\n• Advanced smart TV features"
            },
            {
              question: "When and how does the draw take place?",
              answer: "The draw will take place in December 2026, on a specific date that will be announced to all participants.\n\n• The draw is conducted randomly by a certified system\n• A notary or bailiff may be present to guarantee transparency\n• The draw may be broadcast live on our social networks\n• Results will be published on our website and sent by email\n• Winners will be personally contacted within 7 days"
            },
            {
              question: "How will I know if I won?",
              answer: "If you win, you will be contacted in several ways:\n\n1. Notification email sent to your account address\n2. Phone call from our team\n3. Publication of winning numbers on the 'Winners' page of our site\n4. Announcement on our social networks\n\nMake sure your contact information is up to date in your profile!"
            },
            {
              question: "Can I exchange a prize for money?",
              answer: "No, prizes cannot be exchanged for cash. Prizes will be delivered as described. However, once you have received your prize, you are free to dispose of it as you wish."
            }
          ]
        },
        {
          name: "Payment and Security",
          questions: [
            {
              question: "What payment methods are accepted?",
              answer: "We accept several payment methods, all available now:\n\n💳 International Credit Card (Stripe)\n• Visa, Mastercard, American Express\n• Secure payment via Stripe\n\n💰 PayPal\n• Payment in USD via PayPal\n\n🇨🇦 Interac e-Transfer\n• For Canadian residents\n• Payment in CAD\n\n📱 MONCASH\n• Mobile payment for Haiti\n• In Haitian Gourdes (HTG)\n\n📱 NATCASH\n• Mobile payment for Haiti\n• In Haitian Gourdes (HTG)\n\n₿ Cryptocurrencies\n• Bitcoin, Ethereum, USDT and others\n• Via NOWPayments\n\nAll transactions are encrypted and secure."
            },
            {
              question: "Is the payment secure?",
              answer: "Absolutely! Your security is our priority:\n\n• Payments processed by Stripe, world leader in online payments\n• End-to-end SSL/TLS encryption\n• We NEVER store your credit card data\n• PCI-DSS compliance\n• Instant email confirmation after each purchase"
            },
            {
              question: "Can I get a refund?",
              answer: "Ticket purchases are final and non-refundable. By purchasing a ticket, you are participating in a charitable fundraiser.\n\nException: In case of proven technical problems (double billing, system error), contact us within 24 hours with proof of the problem. Each case will be examined individually.\n\n📧 support@marathon-edenlifeintl.org\n📞 1 866-722-7007"
            }
          ]
        },
        {
          name: "Affiliate Program",
          questions: [
            {
              question: "How does the affiliate program work?",
              answer: "Become an affiliate and earn money by sharing!\n\n1. Log in to your account\n2. Access the Affiliate Space in your dashboard\n3. Get your unique referral code\n4. Share your link on social networks or by email\n5. Earn 10% commission on each sale made\n\nIt's 100% free with no commitment!"
            },
            {
              question: "How much can I earn as an affiliate?",
              answer: "Your earning potential is unlimited!\n\n• Commission: 10% on each sale via your code\n• Example: If someone buys 3 tickets ($100), you earn $10\n• Automatic calculation after each validated sale\n• Monthly commission payments\n• Minimum withdrawal: $100 USD\n\nThe more you share, the more you earn!"
            },
            {
              question: "What tools are available for affiliates?",
              answer: "We provide you with everything you need:\n\n• Personal and unique referral code\n• Personalized link to share\n• Promotional banners to download\n• Dashboard with your real-time statistics\n• Tracking of your sales and commissions\n• Dedicated support for affiliates"
            }
          ]
        },
        {
          name: "Account and Support",
          questions: [
            {
              question: "How do I create an account?",
              answer: "Creating an account is quick and free:\n\n1. Click 'Login' at the top of the page\n2. Choose 'Sign Up'\n3. Fill in your information (name, email, password)\n4. Or sign in directly with Google\n5. Validate your registration\n\nYou can immediately buy tickets!"
            },
            {
              question: "I forgot my password, what do I do?",
              answer: "Don't panic! Contact our support at support@marathon-edenlifeintl.org with your account email address. We will help you reset your password quickly."
            },
            {
              question: "How do I contact support?",
              answer: "Our team is here to help:\n\n📧 Email: support@marathon-edenlifeintl.org\n📞 Phone: 1 866-722-7007\n🕐 Hours: Mon-Fri, 9am-5pm (EST)\n\nWe usually respond within 24-48 business hours.\n\nSupport languages: French, English, Kreyòl"
            }
          ]
        }
      ],
      needMoreHelp: "Need more help?",
      needMoreHelpDesc: "Our team is here to help. Don't hesitate to contact us!",
      contactButton: "Contact Us"
    },
    ht: {
      title: "Kesyon ki Poze Souvan",
      subtitle: "Jwenn repons pou kesyon ou yo sou Maraton EDENLIFE 2026",
      categories: [
        {
          name: "Patisipasyon ak Tikè",
          questions: [
            {
              question: "Kijan pou m patisipe nan Maraton EDENLIFE 2026?",
              answer: "Li senp! Men etap yo:\n\n1. Kreye yon kont gratis sou platfòm nou an ak imel ou\n2. Konekte nan espas pèsonèl ou\n3. Achte tikè ou yo (50 USD chak oswa 3 pou 100 USD)\n4. Peye atravè Stripe, PayPal, Interac, Moncash, Natcash oswa Crypto\n5. Resevwa imedyatman nimewo tikè ou yo pa imel ak yon kòd QR\n\nSe tout! Ou otomatikman enskri pou tiraj desanm 2026 la."
            },
            {
              question: "Konbyen yon tikè koute ak ki òf ki genyen?",
              answer: "Pri nou yo senp ak avantaje:\n\n• 1 tikè: 50 USD\n• 3 tikè: 100 USD (ekonomize 50 USD!)\n\nPlis tikè ou achte, plis chans ou genyen pou genyen. Ou ka achte jiska 100 tikè pa tranzaksyon. Chak tikè gen yon nimewo inik ak yon chans egal pou genyen."
            },
            {
              question: "Èske m ka achte tikè pou yon lòt moun?",
              answer: "Chak kont pèsonèl epi tikè yo asosye ak kont achetè a. Si ou vle bay tikè bay yon moun, moun sa a dwe kreye pwòp kont li. Ou ka transfere lajan ba li pou li fè pwòp acha li. Tikè yo pa ka transfere apre acha."
            },
            {
              question: "Èske gen yon limit sou acha tikè?",
              answer: "Ou ka achte jiska 100 tikè pa tranzaksyon. Pa gen limit sou kantite total tikè ou ka genyen. Plis tikè ou genyen, plis chans ou genyen!"
            }
          ]
        },
        {
          name: "Pri ak Tiraj",
          questions: [
            {
              question: "Ki pri yo genyen?",
              answer: "Nou ofri 5 pri eksepsyonèl:\n\n🏠 1ye Pri - Kay Otonòm (450 000 USD)\n• Kay konplètman meble\n• Sistèm enèji solè 10kW entegre\n• 3 chanm, 2 sal de ben\n• 6 elektwomenaje enkli\n• Salon ak sal manje\n\n🚜 2yèm Pri - Traktè Agrikòl (40 000 USD)\n• Traktè-semè dyezèl 4RM 180 CV\n• Transmisyon plizyè vitès\n• Adaptabilite siperyè ak gwo pisans\n• Pou agrikilti modèn nan gwo echèl ak travay lou\n\n🏍️ 3yèm Pri - Gran Trisikl Kago (5 000 USD)\n• Trisikl Kago 350CC pisans\n• Konplètman ekipe, pare pou itilize\n• Ideyal pou transpò machandiz\n• Kòmanse pwòp biznis ou\n\n💻 4yèm Pri - Sipè Laptop HP Envy (3 000 USD)\n• Pwosesè Intel i7-13700H 14 nwayo\n• Ekran taktil IPS Full HD 17,3 pous\n• 64 Go DDR4, SSD 4 To\n• Grafik Intel Iris Xe\n• Klavye retroeklere, Thunderbolt 4\n• Wi-Fi 6E, kamera enfrawouj 5 MP\n• HDMI 2.1, Windows 10\n\n📺 5yèm Pri - TV LG OLED 65\" (2 000 USD)\n• Televizyon entèlijan LG G5 OLED 4K UHD HDR\n• 65 pous (OLED65G5SUB) – 2025\n• Kalite imaj eksepsyonèl\n• Fonksyon smart TV avanse"
            },
            {
              question: "Kilè ak kijan tiraj la ap fèt?",
              answer: "Tiraj la ap fèt an desanm 2026, nan yon dat espesifik ki pral anonse bay tout patisipan yo.\n\n• Tiraj la fèt o aza pa yon sistèm sètifye\n• Yon notè oswa iyisye ka prezan pou garanti transparans\n• Tiraj la ka difize an dirèk sou rezo sosyal nou yo\n• Rezilta yo ap pibliye sou sit entènèt nou an epi voye pa imel\n• Gayan yo ap kontakte pèsonèlman nan 7 jou"
            },
            {
              question: "Kijan m ap konnen si m genyen?",
              answer: "Si ou genyen, yo ap kontakte ou plizyè fason:\n\n1. Imel notifikasyon voye nan adrès kont ou\n2. Apèl telefòn ekip nou an\n3. Piblikasyon nimewo gayan yo sou paj 'Gayan' sit nou an\n4. Anons sou rezo sosyal nou yo\n\nAsire ou enfòmasyon kontak ou ajou nan pwofil ou!"
            },
            {
              question: "Èske m ka chanje yon pri pou lajan?",
              answer: "Non, pri yo pa ka chanje pou lajan kach. Pri yo ap livre jan yo dekri yo. Sepandan, yon fwa ou resevwa pri ou, ou lib pou fè sa ou vle ak li."
            }
          ]
        },
        {
          name: "Peman ak Sekirite",
          questions: [
            {
              question: "Ki metòd peman yo aksepte?",
              answer: "Nou aksepte plizyè metòd peman, tout disponib kounye a:\n\n💳 Kat kredi entènasyonal (Stripe)\n• Visa, Mastercard, American Express\n• Peman sekirize atravè Stripe\n\n💰 PayPal\n• Peman an USD atravè PayPal\n\n🇨🇦 Virement Interac (e-Transfer)\n• Pou moun ki rete Kanada\n• Peman an CAD\n\n📱 MONCASH\n• Peman mobil pou Ayiti\n• An Goud (HTG)\n\n📱 NATCASH\n• Peman mobil pou Ayiti\n• An Goud (HTG)\n\n₿ Kriptomonè\n• Bitcoin, Ethereum, USDT ak lòt\n• Atravè NOWPayments\n\nTout tranzaksyon yo kripte ak sekirize."
            },
            {
              question: "Èske peman an sekirize?",
              answer: "Absoliman! Sekirite ou se priyorite nou:\n\n• Peman trete pa Stripe, lidè mondyal peman an liy\n• Kriptaj SSL/TLS bout-a-bout\n• Nou PA JANM estoke done kat kredi ou\n• Konfòmite PCI-DSS\n• Konfimasyon imel enstantane apre chak acha"
            },
            {
              question: "Èske m ka jwenn yon ranbousman?",
              answer: "Acha tikè yo final epi pa ka ranbouse. Lè w achte yon tikè, ou ap patisipe nan yon koleksyon fon charitab.\n\nEksepsyon: An ka pwoblèm teknik (doub faktirasyon, erè sistèm), kontakte nou nan 24 èdtan ak prèv pwoblèm nan. Chak ka ap egzamine endividyèlman.\n\n📧 support@marathon-edenlifeintl.org\n📞 1 866-722-7007"
            }
          ]
        },
        {
          name: "Pwogram Afilyasyon",
          questions: [
            {
              question: "Kijan pwogram afilyasyon an fonksyone?",
              answer: "Vin yon afilye epi touche lajan lè w pataje!\n\n1. Konekte nan kont ou\n2. Aksede Espas Afilye nan tablo bò ou\n3. Jwenn kòd parenaj inik ou\n4. Pataje lyen ou sou rezo sosyal oswa pa imel\n5. Touche 10% komisyon sou chak vant ki fèt\n\nLi 100% gratis san angajman!"
            },
            {
              question: "Konbyen m ka touche kòm afilye?",
              answer: "Potansyèl ou pou touche ilimite!\n\n• Komisyon: 10% sou chak vant atravè kòd ou\n• Egzanp: Si yon moun achte 3 tikè (100 USD), ou touche 10 USD\n• Kalkil otomatik apre chak vant valide\n• Peman komisyon chak mwa\n• Minimòm retrè: 100 USD\n\nPlis ou pataje, plis ou touche!"
            },
            {
              question: "Ki zouti ki disponib pou afilye yo?",
              answer: "Nou ba ou tout sa ou bezwen:\n\n• Kòd parenaj pèsonèl ak inik\n• Lyen pèsonalize pou pataje\n• Banyè pwomosyonèl pou telechaje\n• Tablo bò ak estatistik ou yo an tan reyèl\n• Swivi vant ak komisyon ou yo\n• Sipò dedye pou afilye yo"
            }
          ]
        },
        {
          name: "Kont ak Sipò",
          questions: [
            {
              question: "Kijan pou m kreye yon kont?",
              answer: "Kreye yon kont rapid epi gratis:\n\n1. Klike sou 'Konekte' anwo paj la\n2. Chwazi 'Enskri'\n3. Ranpli enfòmasyon ou yo (non, imel, modpas)\n4. Oswa konekte dirèkteman ak Google\n5. Valide enskripsyon ou\n\nOu ka imedyatman achte tikè!"
            },
            {
              question: "Mwen bliye modpas mwen, kisa pou m fè?",
              answer: "Pa paniké! Kontakte sipò nou nan support@marathon-edenlifeintl.org ak adrès imel kont ou. Nou ap ede ou reyinisyalize modpas ou rapidman."
            },
            {
              question: "Kijan pou m kontakte sipò?",
              answer: "Ekip nou la pou ede ou:\n\n📧 Imel: support@marathon-edenlifeintl.org\n📞 Telefòn: 1 866-722-7007\n🕐 Èdtan: Lendi-Vandredi, 9è-5è (EST)\n\nNou jeneralman reponn nan 24-48 èdtan travay.\n\nLang sipò: Français, English, Kreyòl"
            }
          ]
        }
      ],
      needMoreHelp: "Bezwen plis èd?",
      needMoreHelpDesc: "Ekip nou la pou ede ou. Pa ezite kontakte nou!",
      contactButton: "Kontakte Nou"
    },
    es: {
      title: "Preguntas Frecuentes",
      subtitle: "Encuentre respuestas rápidas a todas sus preguntas",
      searchPlaceholder: "Buscar una pregunta...",
      categories: [
        {
          name: "Participación y Boletos",
          questions: [
            { question: "¿Cómo participo en el Maratón EDENLIFE 2026?", answer: "¡Es simple! Aquí están los pasos:\n\n1. Cree una cuenta gratuita con su correo\n2. Inicie sesión en su espacio personal\n3. Compre sus boletos ($50 c/u o 3 por $100)\n4. Pague vía Stripe, PayPal, Interac, Moncash, Natcash o Crypto\n5. Reciba inmediatamente sus números de boleto por correo con código QR\n\n¡Eso es todo! Está automáticamente inscrito para el sorteo de diciembre 2026." },
            { question: "¿Cuánto cuesta un boleto y cuáles son las ofertas?", answer: "Nuestros precios son simples y ventajosos:\n\n• 1 boleto: $50 USD\n• 3 boletos: $100 USD (¡ahorre $50!)\n\nMás boletos compre, mayores sus probabilidades de ganar." },
            { question: "¿Puedo comprar boletos para otra persona?", answer: "Cada cuenta es personal y los boletos están asociados a la cuenta del comprador. Si desea regalar boletos, esa persona debe crear su propia cuenta." },
            { question: "¿Hay un límite de compra de boletos?", answer: "Puede comprar hasta 100 boletos por transacción. No hay límite en el total de boletos que puede tener." }
          ]
        },
        {
          name: "Premios y Sorteo",
          questions: [
            { question: "¿Cuáles son los premios a ganar?", answer: "5 premios extraordinarios:\n\n🏠 1er Premio: Casa Autosuficiente (450,000 USD)\n🚜 2do Premio: Tractor Agrícola (40,000 USD)\n🛺 3er Premio: Triciclo de Carga (5,000 USD)\n💻 4to Premio: Super Laptop HP Envy (3,000 USD)\n📺 5to Premio: TV LG OLED 65\" (2,000 USD)" },
            { question: "¿Cuándo será el sorteo?", answer: "El sorteo se realizará en diciembre 2026. La fecha exacta se comunicará. ¡Todos los boletos participan!" },
            { question: "¿Cómo se realiza el sorteo?", answer: "Sorteo público y transparente mediante software aleatorio certificado, filmado y transmitido en vivo. Notario presente." },
            { question: "¿Cómo sabré si gané?", answer: "Contactaremos a los ganadores por email y teléfono dentro de 7 días. ¡Los resultados también se publicarán en el sitio!" }
          ]
        },
        {
          name: "Pagos y Seguridad",
          questions: [
            { question: "¿Qué métodos de pago aceptan?", answer: "Aceptamos varios métodos de pago, todos disponibles:\n\n💳 Tarjeta bancaria internacional (Stripe)\n💰 PayPal (USD)\n🇨🇦 Interac e-Transfer (CAD)\n📱 MONCASH (Haití - HTG)\n📱 NATCASH (Haití - HTG)\n₿ Criptomonedas (Bitcoin, Ethereum, USDT)\n\nTodas las transacciones son seguras y encriptadas." },
            { question: "¿Es seguro el pago?", answer: "Absolutamente. Usamos Stripe, líder mundial en pagos en línea. Certificación PCI-DSS, encriptación SSL, autenticación 3D Secure." },
            { question: "¿Puedo obtener reembolso?", answer: "No, las compras son DEFINITIVAS y NO REEMBOLSABLES." }
          ]
        },
        {
          name: "Programa de Afiliados",
          questions: [
            { question: "¿Qué es el programa de afiliados?", answer: "¡Gane 10% de comisión sobre cada venta mediante su código de referido! Seguimiento automático, comisiones transparentes, pagos mensuales." },
            { question: "¿Cómo me convierto en afiliado?", answer: "1. Cree una cuenta\n2. Vaya a su perfil\n3. Active su código de afiliado\n4. Comparta su enlace único\n5. Gane 10% por cada venta" },
            { question: "¿Cuándo recibo mis comisiones?", answer: "Pagos mensuales el día 15 del mes siguiente. Mínimo para retiro: $100 USD. Métodos: transferencia bancaria o PayPal." }
          ]
        },
        {
          name: "Cuenta y Soporte",
          questions: [
            { question: "¿Cómo creo una cuenta?", answer: "¡Rápido y gratis!\n\n1. Clic en 'Conectar'\n2. Elija 'Registrarse'\n3. Complete su información\n4. O conéctese con Google\n5. ¡Listo!" },
            { question: "Olvidé mi contraseña, ¿qué hago?", answer: "Contacte soporte en support@marathon-edenlifeintl.org con el email de su cuenta. Le ayudaremos a restablecerla." },
            { question: "¿Cómo contacto soporte?", answer: "📧 Email: support@marathon-edenlifeintl.org\n📞 Teléfono: 1 866-722-7007\n🕐 Horario: Lun-Vie, 9am-5pm (EST)\n\nIdiomas: Francés, Inglés, Kreyòl" }
          ]
        }
      ],
      needMoreHelp: "¿Necesita más ayuda?",
      needMoreHelpDesc: "Nuestro equipo está aquí para ayudarle. ¡No dude en contactarnos!",
      contactButton: "Contáctenos"
    },
    pt: {
      title: "Perguntas Frequentes",
      subtitle: "Encontre respostas rápidas para todas as suas perguntas",
      searchPlaceholder: "Pesquisar uma pergunta...",
      categories: [
        {
          name: "Participação e Bilhetes",
          questions: [
            { question: "Como participo da Maratona EDENLIFE 2026?", answer: "É simples! Aqui estão os passos:\n\n1. Crie uma conta gratuita com seu email\n2. Entre no seu espaço pessoal\n3. Compre seus bilhetes ($50 cada ou 3 por $100)\n4. Pague via Stripe, PayPal, Interac, Moncash, Natcash ou Crypto\n5. Receba imediatamente seus números de bilhete por email com QR code\n\nIsso é tudo! Você está automaticamente inscrito para o sorteio de dezembro 2026." },
            { question: "Quanto custa um bilhete e quais são as ofertas?", answer: "Nossos preços são simples e vantajosos:\n\n• 1 bilhete: $50 USD\n• 3 bilhetes: $100 USD (economize $50!)\n\nQuanto mais bilhetes comprar, maiores suas chances de ganhar." },
            { question: "Posso comprar bilhetes para outra pessoa?", answer: "Cada conta é pessoal e os bilhetes estão associados à conta do comprador. Se deseja presentear bilhetes, essa pessoa deve criar sua própria conta." },
            { question: "Há um limite de compra de bilhetes?", answer: "Pode comprar até 100 bilhetes por transação. Não há limite no total de bilhetes que pode ter." }
          ]
        },
        {
          name: "Prêmios e Sorteio",
          questions: [
            { question: "Quais são os prêmios a ganhar?", answer: "5 prêmios extraordinários:\n\n🏠 1º Prêmio: Casa Autossuficiente (450.000 USD)\n🚜 2º Prêmio: Trator Agrícola (40.000 USD)\n🛺 3º Prêmio: Triciclo de Carga (5.000 USD)\n💻 4º Prêmio: Super Laptop HP Envy (3.000 USD)\n📺 5º Prêmio: TV LG OLED 65\" (2.000 USD)" },
            { question: "Quando será o sorteio?", answer: "O sorteio será realizado em dezembro 2026. A data exata será comunicada. Todos os bilhetes participam!" },
            { question: "Como é feito o sorteio?", answer: "Sorteio público e transparente usando software aleatório certificado, filmado e transmitido ao vivo. Notário presente." },
            { question: "Como saberei se ganhei?", answer: "Contataremos os ganhadores por email e telefone em 7 dias. Os resultados também serão publicados no site!" }
          ]
        },
        {
          name: "Pagamentos e Segurança",
          questions: [
            { question: "Quais métodos de pagamento aceitam?", answer: "Aceitamos vários métodos de pagamento, todos disponíveis:\n\n💳 Cartão bancário internacional (Stripe)\n💰 PayPal (USD)\n🇨🇦 Interac e-Transfer (CAD)\n📱 MONCASH (Haiti - HTG)\n📱 NATCASH (Haiti - HTG)\n₿ Criptomoedas (Bitcoin, Ethereum, USDT)\n\nTodas as transações são seguras e criptografadas." },
            { question: "O pagamento é seguro?", answer: "Absolutamente. Usamos Stripe, líder mundial em pagamentos online. Certificação PCI-DSS, criptografia SSL, autenticação 3D Secure." },
            { question: "Posso obter reembolso?", answer: "Não, as compras são DEFINITIVAS e NÃO REEMBOLSÁVEIS." }
          ]
        },
        {
          name: "Programa de Afiliados",
          questions: [
            { question: "O que é o programa de afiliados?", answer: "Ganhe 10% de comissão sobre cada venda através do seu código de indicação! Rastreamento automático, comissões transparentes, pagamentos mensais." },
            { question: "Como me torno afiliado?", answer: "1. Crie uma conta\n2. Vá ao seu perfil\n3. Ative seu código de afiliado\n4. Compartilhe seu link único\n5. Ganhe 10% por cada venda" },
            { question: "Quando recebo minhas comissões?", answer: "Pagamentos mensais no dia 15 do mês seguinte. Mínimo para saque: $100 USD. Métodos: transferência bancária ou PayPal." }
          ]
        },
        {
          name: "Conta e Suporte",
          questions: [
            { question: "Como crio uma conta?", answer: "Rápido e gratuito!\n\n1. Clique em 'Conectar'\n2. Escolha 'Registrar'\n3. Preencha suas informações\n4. Ou conecte-se com Google\n5. Pronto!" },
            { question: "Esqueci minha senha, o que faço?", answer: "Contate suporte em support@marathon-edenlifeintl.org com o email da sua conta. Ajudaremos a redefinir." },
            { question: "Como contato o suporte?", answer: "📧 Email: support@marathon-edenlifeintl.org\n📞 Telefone: 1 866-722-7007\n🕐 Horário: Seg-Sex, 9h-17h (EST)\n\nIdiomas: Francês, Inglês, Kreyòl" }
          ]
        }
      ],
      needMoreHelp: "Precisa de mais ajuda?",
      needMoreHelpDesc: "Nossa equipe está aqui para ajudá-lo. Não hesite em nos contatar!",
      contactButton: "Entre em Contato"
    }
  };

  const t = faqData[i18n.language] || faqData.fr;
  
  // Flatten all questions for display
  const allQuestions = t.categories.flatMap((cat, catIndex) => 
    cat.questions.map((q, qIndex) => ({
      ...q,
      category: cat.name,
      globalIndex: t.categories.slice(0, catIndex).reduce((acc, c) => acc + c.questions.length, 0) + qIndex
    }))
  );

  return (
    <div className="min-h-screen pt-24 px-6 pb-12" style={{ background: 'linear-gradient(135deg, #F4F1DE 0%, #E8E3D3 100%)' }}>
      <div className="max-w-4xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="text-center mb-12">
            <HelpCircle className="w-20 h-20 text-accent mx-auto mb-4" />
            <h1 className="text-4xl md:text-5xl font-bold text-primary mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              {t.title}
            </h1>
            <p className="text-lg text-gray-600">{t.subtitle}</p>
          </div>

          {/* Categories */}
          {t.categories.map((category, catIndex) => (
            <div key={catIndex} className="mb-8">
              <h2 className="text-xl font-bold text-primary mb-4 px-2">{category.name}</h2>
              <div className="space-y-3">
                {category.questions.map((faq, qIndex) => {
                  const globalIndex = t.categories.slice(0, catIndex).reduce((acc, c) => acc + c.questions.length, 0) + qIndex;
                  return (
                    <motion.div
                      key={qIndex}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: globalIndex * 0.03 }}
                      className="bg-white/90 backdrop-blur-md rounded-2xl shadow-lg overflow-hidden"
                    >
                      <button
                        onClick={() => setOpenIndex(openIndex === globalIndex ? -1 : globalIndex)}
                        className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-primary/5 transition-colors"
                      >
                        <span className="font-semibold text-primary pr-4">{faq.question}</span>
                        <ChevronDown
                          className={`w-5 h-5 text-primary transition-transform flex-shrink-0 ${
                            openIndex === globalIndex ? 'transform rotate-180' : ''
                          }`}
                        />
                      </button>
                      {openIndex === globalIndex && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          className="px-6 pb-5"
                        >
                          <p className="text-gray-700 leading-relaxed whitespace-pre-line">{faq.answer}</p>
                        </motion.div>
                      )}
                    </motion.div>
                  );
                })}
              </div>
            </div>
          ))}

          {/* Contact CTA */}
          <div className="mt-12 bg-gradient-to-br from-primary to-accent rounded-3xl p-8 text-white text-center">
            <h3 className="text-2xl font-bold mb-4">{t.needMoreHelp}</h3>
            <p className="mb-6 text-white/90">{t.needMoreHelpDesc}</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="mailto:support@marathon-edenlifeintl.org" 
                className="inline-flex items-center justify-center bg-white text-primary px-6 py-3 rounded-full font-semibold hover:bg-white/90 transition-colors"
              >
                <Mail className="w-5 h-5 mr-2" />
                support@marathon-edenlifeintl.org
              </a>
              <a 
                href="tel:+18667227007" 
                className="inline-flex items-center justify-center bg-white/20 text-white px-6 py-3 rounded-full font-semibold hover:bg-white/30 transition-colors"
              >
                <Phone className="w-5 h-5 mr-2" />
                1 866-722-7007
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default FAQ;
